/**
 * V55.22: 좌석배치도 툴바 컴포넌트
 *
 * A/B 탭 제거, 편집 모드 전용 UI
 * - 보기 모드: [편집] 버튼만
 * - 편집 모드: [저장] [편집 종료] (V55.21에서 덮어쓰기 버튼 추가됨)
 *
 * V55.22: 복사 버튼 UI 제거 (덮어쓰기 기능으로 대체됨)
 *
 * @module pages/organization/components/seatMap/SeatMapToolbar
 */

import React from 'react';
import { Edit, Save, X } from 'lucide-react';

// ============================================
// Props 타입
// ============================================

interface SeatMapToolbarProps {
  /** 뷰 모드 (view: 보기, edit: 편집) */
  viewMode: 'view' | 'edit';

  /** 편집 권한 여부 */
  canEdit: boolean;

  /** 미저장 변경사항 여부 */
  hasUnsavedChanges: boolean;

  /** 저장 중 여부 */
  isSaving: boolean;

  /** @deprecated V55.22: 덮어쓰기 기능으로 대체됨 */
  isCopying?: boolean;

  /** 편집 모드 진입 핸들러 (템플릿 모달 열기) */
  onEnterEditMode: () => void;

  /** 편집 모드 종료 핸들러 */
  onExitEditMode: () => void;

  /** 저장 핸들러 */
  onSave: () => void;

  /** @deprecated V55.22: 덮어쓰기 기능으로 대체됨 */
  onCopyLiveToDraft?: () => void;
}

// ============================================
// 툴바 컴포넌트
// ============================================

export function SeatMapToolbar({
  viewMode,
  canEdit,
  hasUnsavedChanges,
  isSaving,
  isCopying = false, // V55.22: deprecated, optional
  onEnterEditMode,
  onExitEditMode,
  onSave,
  // onCopyLiveToDraft, // V55.22: deprecated, unused
}: SeatMapToolbarProps) {
  const isProcessing = isSaving || isCopying;
  const isEditMode = viewMode === 'edit';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {/* 보기 모드: 편집 버튼만 */}
      {!isEditMode && canEdit && (
        <button
          onClick={onEnterEditMode}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            border: 'none',
            borderRadius: '8px',
            background: 'var(--color-primary)',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
        >
          <Edit size={16} />
          편집
        </button>
      )}

      {/* 편집 모드: 저장, 복사, 종료 */}
      {isEditMode && (
        <>
          {/* 편집 중 상태 뱃지 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '16px',
              background: 'var(--color-amber-100)',
              color: 'var(--color-amber-700)',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            <Edit size={14} />
            편집 중
          </div>

          {/* 저장 버튼 */}
          <button
            onClick={onSave}
            disabled={isProcessing || !hasUnsavedChanges}
            title="현재 작업 저장"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '8px',
              background: hasUnsavedChanges ? '#22c55e' : '#94a3b8',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isProcessing || !hasUnsavedChanges ? 'not-allowed' : 'pointer',
              opacity: isProcessing ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
          >
            <Save size={16} />
            {isSaving ? '저장 중...' : '저장'}
          </button>

          {/* V55.22: 복사 버튼 제거됨 - 덮어쓰기 기능으로 대체 (SeatMapPage에서 처리) */}

          {/* 편집 종료 버튼 */}
          <button
            onClick={onExitEditMode}
            disabled={isProcessing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              background: 'white',
              color: '#64748b',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <X size={16} />
            편집 종료
          </button>

          {/* 미저장 표시 */}
          {hasUnsavedChanges && (
            <span
              style={{
                fontSize: '12px',
                color: '#f59e0b',
                fontWeight: '500',
              }}
            >
              * 저장되지 않은 변경사항
            </span>
          )}
        </>
      )}
    </div>
  );
}
